import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const OTP_API_URL = "https://servermail.dietz.asia";
const OTP_SEND_DEBOUNCE_MS = 3000;

const Container = styled.div`
  height: 100vh;
  background-color: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  font-family: "Prompt", sans-serif;
  overflow: hidden;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
`;

const Card = styled.div`
  background-color: white;
  border-radius: 0.5rem;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1),
    0 4px 6px -2px rgba(0, 0, 0, 0.05);
  padding: 2rem 1.5rem;
  width: 100%;
  max-width: 20rem;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 1.5rem;
`;

const Title = styled.h1`
  font-size: 1.5rem;
  font-weight: bold;
  color: #06b6d4;
  margin-bottom: 0.3rem;
`;

const Subtitle = styled.h2`
  font-size: 1.1rem;
  color: #00428c;
  font-weight: 300;
  margin-top: 0;
`;

const IconWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin: 1rem 0 0.75rem;
`;

const Description = styled.p`
  text-align: center;
  font-size: 0.875rem;
  color: #374151;
  margin-bottom: 1.25rem;
  line-height: 1.6;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid ${(props) => (props.$error ? "#ef4444" : "#d1d5db")};
  border-radius: 0.5rem;
  font-size: 1rem;
  box-sizing: border-box;
  text-align: center;
  letter-spacing: 0.25rem;
  font-weight: 600;

  &:focus {
    outline: none;
    border-color: #06b6d4;
    box-shadow: 0 0 0 3px rgba(6, 182, 212, 0.1);
  }
`;

const ErrorText = styled.p`
  color: #ef4444;
  font-size: 0.8rem;
  text-align: center;
  margin: 0.4rem 0 0.75rem;
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 0.75rem 1rem;
  border-radius: 0.5rem;
  font-weight: 500;
  color: #ffffff;
  border: none;
  cursor: pointer;
  transition: background-color 0.2s;
  background-color: #00428c;
  font-size: 1rem;
  margin-top: 0.5rem;

  ${(props) =>
    props.disabled
      ? `
    background-color: #9ca3af;
    cursor: not-allowed;
  `
      : `
    &:hover {
      background-color: #003366;
    }
  `}
`;

const ResendSection = styled.div`
  text-align: center;
  margin-top: 1.25rem;
  font-size: 0.8rem;
  color: #6b7280;
`;

const ResendLink = styled.span`
  color: #06b6d4;
  cursor: ${(props) => (props.$disabled ? "not-allowed" : "pointer")};
  opacity: ${(props) => (props.$disabled ? 0.5 : 1)};
  font-weight: 500;

  &:hover {
    text-decoration: ${(props) => (props.$disabled ? "none" : "underline")};
  }
`;

const BackLink = styled.div`
  text-align: center;
  margin-top: 0.75rem;
  font-size: 0.8rem;
  color: #6b7280;
  cursor: pointer;

  &:hover {
    color: #374151;
    text-decoration: underline;
  }
`;

const EmailIcon = () => (
  <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="4" y="14" width="56" height="38" rx="4" fill="#06b6d4" />
    <path d="M4 20L32 38L60 20" stroke="white" strokeWidth="3" strokeLinecap="round" />
    <circle cx="48" cy="44" r="10" fill="white" stroke="#06b6d4" strokeWidth="2" />
    <path d="M43 44L47 48L54 41" stroke="#06b6d4" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const OtpPage = () => {
  const navigate = useNavigate();
  const [otp, setOtp] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const [otpToken, setOtpToken] = useState(null);
  const timerRef = useRef(null);

  const otpSession = (() => {
    try {
      return JSON.parse(sessionStorage.getItem("otp_pending") || "null");
    } catch {
      return null;
    }
  })();

  const getOtpSendKey = () => {
    return otpSession?.email ? `otp_last_send_${otpSession.email}` : null;
  };

  const clearOtpSendLock = () => {
    const sendKey = getOtpSendKey();
    if (sendKey) {
      sessionStorage.removeItem(sendKey);
    }
  };

  useEffect(() => {
    if (!otpSession) {
      navigate("/login", { replace: true });
      return;
    }
    startCountdown();
    sendOtp();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const startCountdown = () => {
    setCountdown(60);
    setCanResend(false);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          setCanResend(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const sendOtp = async (force = false) => {
    const sendKey = getOtpSendKey();
    if (!sendKey) return;

    const lastSentAt = Number(sessionStorage.getItem(sendKey) || 0);
    const now = Date.now();
    if (!force && now - lastSentAt < OTP_SEND_DEBOUNCE_MS) {
      return;
    }

    sessionStorage.setItem(sendKey, String(now));

    try {
      const response = await axios.post(OTP_API_URL, {
        action: "send",
        email: otpSession?.email,
      });
      if (response.data?.result && response.data?.token) {
        setOtpToken(response.data.token);
      }
    } catch (err) {
      console.error("Send OTP error:", err);
      sessionStorage.removeItem(sendKey);
    }
  };

  const handleVerify = async () => {
    if (!otp || otp.length < 4) {
      setError("กรุณากรอกรหัส OTP ให้ครบถ้วน");
      return;
    }
    if (!otpToken) {
      setError("ไม่พบ token กรุณาขอรหัส OTP ใหม่");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const response = await axios.post(OTP_API_URL, {
        action: "verify",
        token: otpToken,
        otp: otp,
      });
      if (response.data?.result === true) {
        clearOtpSendLock();
        const redirectTo = otpSession?.redirectTo || "/health-groups";
        sessionStorage.removeItem("otp_pending");
        navigate(redirectTo, { replace: true });
      } else {
        setError("รหัสไม่ถูกต้องโปรดลองอีกครั้ง");
        setOtp("");
      }
    } catch (err) {
      setError("รหัสไม่ถูกต้องโปรดลองอีกครั้ง");
      setOtp("");
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!canResend) return;
    setOtp("");
    setError("");
    setOtpToken(null);
    startCountdown();
    await sendOtp(true);
  };

  const handleBackToLogin = () => {
    clearOtpSendLock();
    sessionStorage.removeItem("otp_pending");
    localStorage.removeItem("mfu_token");
    localStorage.removeItem("mfu_user");
    navigate("/login", { replace: true });
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") handleVerify();
  };

  if (!otpSession) return null;

  return (
    <Container>
      <Card>
        <Header>
          <Title>
            <img src="/logo_dietz.png" alt="Dietz Logo" style={{ height: "30px" }} />
          </Title>
          <Subtitle>เข้าสู่ระบบ</Subtitle>
        </Header>

        <IconWrapper>
          <EmailIcon />
        </IconWrapper>

        <Description>
          กรุณาใส่รหัส OTP ที่ส่งไปยังอีเมลของท่าน
          <br />
          ที่ได้ลงทะเบียนเพื่อยืนยันตัวตน
        </Description>

        <Input
          type="text"
          value={otp}
          onChange={(e) => {
            const val = e.target.value.replace(/\D/g, "").slice(0, 6);
            setOtp(val);
            setError("");
          }}
          onKeyPress={handleKeyPress}
          placeholder=""
          $error={!!error}
          maxLength={6}
          autoFocus
        />

        {error && <ErrorText>{error}</ErrorText>}

        <SubmitButton onClick={handleVerify} disabled={loading || !otp}>
          {loading ? "กำลังตรวจสอบ..." : "เข้าสู่ระบบ"}
        </SubmitButton>

        <ResendSection>
          หากไม่ได้รับรหัส OTP ให้ส่งใหม่อีกครั้ง
          <br />
          <ResendLink $disabled={!canResend} onClick={handleResend}>
            {canResend ? "ขอรหัสใหม่" : `ขอรหัสใหม่ (${countdown})`}
          </ResendLink>
        </ResendSection>

        <BackLink onClick={handleBackToLogin}>
          &larr; กลับหน้าเข้าสู่ระบบ
        </BackLink>
      </Card>
    </Container>
  );
};

export default OtpPage;
